import React from 'react';
import {useTranslation} from 'react-i18next';
import {EAutocompleteOption} from 'webapp-lib/Components';

import {EFAutocompleteProps} from '_/utilities/form';
import {PageItem} from '_/utilities/types';

import {AutocompleteBase} from './AutocompleteBase/AutocompleteBase';
import {ContactAutocompleteProps} from './types';
import {buildSearchContacts, disableSelectedOptions} from './utils';

type ContactItem = {id: string};

const CREATE_NEW_CONTACT = 'createNewContact';

export const ContactAutocomplete = <
  T extends ContactItem,
  EXTRA_PARAMS extends object,
>({
  onContactSelection,
  appendCreateContactOption = false,
  filterContacts,
  loadContactCall,
  loadContactCallInitialExtraParams,
  mapToOptions,
  alreadySelectedContactIds = [],
  reloadOptionsTrigger = false,
  onSelectedIdMissing,
  ...otherProps
}: Omit<EFAutocompleteProps, 'options'> & {
  alreadySelectedContactIds?: string[];
  reloadOptionsTrigger?: boolean | string;
  onSelectedIdMissing?: () => void;
} & ContactAutocompleteProps<T, EXTRA_PARAMS>): React.ReactElement => {
  const {t} = useTranslation('common');

  const applyFilterContacts = (contacts: T[]): T[] => {
    if (!filterContacts) {
      return contacts;
    }
    const pageItems: PageItem<T>[] = contacts.map((item) => ({
      cursor: '',
      item,
    }));
    return filterContacts(pageItems).map((pageItem) => pageItem.item);
  };

  const mapOptions = (contacts: T[]): EAutocompleteOption[] => [
    ...disableSelectedOptions(
      applyFilterContacts(contacts).map(mapToOptions),
      alreadySelectedContactIds
    ),
    ...(appendCreateContactOption
      ? [
          {
            value: CREATE_NEW_CONTACT,
            displayText: t('actions.createNewContact'),
            isPlaceholderOption: true,
          },
        ]
      : []),
  ];

  const handleItemChange = (
    item: T | undefined,
    {value, searchQuery}: {value: unknown; searchQuery: string}
  ) => {
    if (!onContactSelection) {
      return;
    }
    const selectedExtraContact =
      value === CREATE_NEW_CONTACT ? {id: CREATE_NEW_CONTACT} : null;
    onContactSelection(
      (item ?? selectedExtraContact ?? value) as T,
      searchQuery
    );
  };

  return (
    <AutocompleteBase
      {...otherProps}
      loadItems={buildSearchContacts(
        loadContactCall,
        loadContactCallInitialExtraParams
      )}
      mapOptions={mapOptions}
      reloadTrigger={reloadOptionsTrigger}
      toOption={mapToOptions}
      noOptionFiltering
      onItemChange={handleItemChange}
      onSelectedItemMissing={onSelectedIdMissing}
    />
  );
};
